import { useEffect, useMemo, useState } from "react";
import SearchableDropdown from "../../../../../../../components/SearchableDropdown/SearchableDropdown";
import Button from "../../../../../../../components/Button/Button";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
import CloseIcon from "../../../../../../../assets/svg/close-icon";
import { useGetHelpdeskResolutionReasonsQuery } from "../../../../../../../redux/services/helpdeskApi";
import { getHelpdeskLabels } from "../../../../../../../utils/constants/mappings";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../redux/store";

interface ResolutionReasonModalProps {
  open: boolean;
  onSubmit: (resolutionReason: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
  initialValue?: string | null;
}

const ResolutionReasonModal = ({
  open,
  onSubmit,
  onCancel,
  isLoading = false,
  initialValue = null,
}: ResolutionReasonModalProps) => {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region ?? "UK");
  useEffect(() => {
    if (open) {
      setSelectedReason(initialValue ?? null);
    }
  }, [open, initialValue]);

  const {
    data: resolutionReasons,
    isLoading: isResolutionsLoading,
    isFetching: isResolutionsFetching,
  } = useGetHelpdeskResolutionReasonsQuery({}, { skip: !open });

  const resolutionOptions = useMemo(() => {
    if (isResolutionsLoading || isResolutionsFetching) return [];

    const raw = resolutionReasons as
      | { reasons?: string[] }
      | string[]
      | undefined;
    const reasons = Array.isArray(raw) ? raw : (raw?.reasons ?? []);
    return reasons.map((reason: string) => ({
      value: reason,
      content: (
        <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
          {reason}
        </span>
      ),
      selectedOptionContent: (
        <div className="flex items-center gap-1.5">
          <VerifiedIcon className="w-3.5 h-3.5 text-verba-green-success dark:text-verba-green-success-dk shrink-0" />
          <span className="text-md font-medium text-verba-indigo dark:text-verba-indigo-dk">
            {reason}
          </span>
        </div>
      ),
    }));
  }, [isResolutionsLoading, isResolutionsFetching, resolutionReasons]);

  const handleCancel = () => {
    setSelectedReason(null);
    onCancel();
  };

  const handleSubmit = () => {
    if (!selectedReason) return;
    onSubmit(selectedReason);
    setSelectedReason(null);
  };

  const handleBackdropClick = () => {
    if (!isLoading) handleCancel();
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 z-1000"
        onClick={handleBackdropClick}
      />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-1001 rounded-[8px] shadow-popper-blur-effect backdrop-blur-[20px] w-[312px] max-w-[90vw] max-h-[90vh] overflow-visible bg-pure-white dark:bg-pure-white-dk border border-border-gray dark:border-border-gray-dk">
        <div className="p-6">
          <div className="flex flex-col gap-9">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-[5px]">
                  <VerifiedIcon className="w-4 h-4 text-verba-green-success dark:text-verba-green-success-dk" />
                  <span className="text-heading-text dark:text-heading-text-dk font-semibold text-base">
                    Mark as Closed
                  </span>
                </div>
                <button
                  onClick={handleCancel}
                  className="shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-110 cursor-pointer"
                >
                  <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
                </button>
              </div>

              <p className="text-sm font-normal text-verba-field-label dark:text-verba-field-label-dk leading-relaxed">
                Please select a Resolution if you want to close this {helpdeskLabels.ticketSingularLower}.
              </p>

              <div className="flex flex-col gap-1.5">
                <label className="text-md font-normal text-verba-indigo dark:text-verba-indigo-dk">
                  Select Resolution
                </label>
                <SearchableDropdown
                  id="resolution-reason-dropdown"
                  options={resolutionOptions}
                  value={selectedReason}
                  onChange={(value) => setSelectedReason(value)}
                  disabled={
                    isResolutionsLoading || isResolutionsFetching || isLoading
                  }
                  placeholder={
                    isResolutionsLoading || isResolutionsFetching
                      ? "Loading..."
                      : "Select"
                  }
                  className="w-full"
                  triggerClassName="!rounded-lg !h-[36px] !border-border-gray dark:!border-border-gray-dk"
                  selectedOptionContainerClassName="!px-3 !py-0 h-full"
                  dropdownClassName="!bg-popper-bg-fixed-white dark:!bg-popper-bg-fixed-white-dk !py-0"
                  dropdownMenuClassName="!max-h-[150px]"
                  optionClassName="!py-[8px]"
                  optionsListClassName="!py-0"
                  placeholderClassName="!text-md !text-gray-placeholder dark:!text-gray-placeholder-dk"
                  ariaLabel="Resolution Reason"
                  sectionHeading="Resolutions"
                  searchInputClassName="!rounded-t-[8px]"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2">
              <Button
                innerText="Cancel"
                onClick={handleCancel}
                disabled={isLoading}
                buttonContainerClass="min-w-[100px] min-h-[26px] py-[2px] px-2 text-md border border-verba-indigo dark:border-verba-indigo-dk bg-verba-indigo dark:bg-verba-indigo-dk rounded-md cursor-pointer"
                innerTextClass="text-pure-white dark:text-pure-white-dk text-md font-semibold"
              />
              <Button
                innerText="Mark as Closed"
                onClick={handleSubmit}
                disabled={isLoading || !selectedReason}
                isLoading={isLoading}
                buttonContainerClass="disabled:opacity-30 min-h-[26px] py-[2px] px-4 text-md border border-verba-green-success dark:border-verba-green-success-dk bg-transparent dark:bg-transparent rounded-md cursor-pointer hover:bg-verba-green-success dark:hover:bg-verba-green-success-dk group"
                innerTextClass="text-verba-green-success dark:text-verba-green-success-dk text-md font-semibold group-hover:text-pure-white dark:group-hover:text-pure-white-dk"
                loaderColor="text-verba-green-success dark:text-verba-green-success-dk"
                loaderSize={15}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResolutionReasonModal;
